import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Check,
  Crown,
  GraduationCap,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { callGetPublicStats } from '@/lib/api';

// Public landing page — keep it simple but organised.
//
// Layout (top to bottom): sticky nav bar → hero with twin CTAs → trust
// strip with concrete numbers → 3 feature cards → 3-step "how it works"
// → 2-card pricing → minimal footer.
//
// Visual rhythm alternates `bg-white` and `bg-stone-50` per section in light
// mode (subtle on dark) so the page reads as distinct chunks instead of one
// wall of content. Section headers use the same H2 pattern with a small
// eyebrow tag above so each block has the same shape.

export function LandingPage() {
  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100">
      <NavBar />
      <Hero />
      <TrustStrip />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection />
      <Footer />
    </div>
  );
}

// ============================================================
//  NAV BAR — sticky, slim, accurate links
// ============================================================

function NavBar() {
  return (
    <header className="sticky top-0 z-30 border-b border-stone-200 dark:border-stone-800 bg-stone-50/90 dark:bg-stone-950/90 backdrop-blur">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/" className="text-lg font-semibold text-brand-700 dark:text-brand-400 tracking-tight">
          CNA Prep Pro
        </Link>
        <nav className="flex items-center gap-2 sm:gap-5 text-sm">
          <a href="#how" className="hidden sm:inline text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100">
            How it works
          </a>
          <a href="#pricing" className="hidden sm:inline text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100">
            Pricing
          </a>
          <Link to="/signin" className="text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100">
            Sign in
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center gap-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 font-semibold shadow-sm transition-colors"
          >
            Sign up
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </nav>
      </div>
    </header>
  );
}

// ============================================================
//  HERO — single headline, two clearly-different CTAs
// ============================================================

function Hero() {
  return (
    <section className="px-4 sm:px-6 pt-14 pb-12 sm:pb-16 max-w-3xl mx-auto text-center">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1]">
        Pass your CNA exam.<br />
        <span className="text-brand-700 dark:text-brand-400">First time.</span>
      </h1>
      <p className="mt-5 text-stone-700 dark:text-stone-300 text-base sm:text-lg max-w-prose mx-auto">
        Clear practice questions with useful feedback
        on every answer and a readiness score that tells you when you're ready.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link to="/trial/new" className="w-full sm:w-auto">
          <Button size="lg" className="w-full sm:w-auto px-7">
            <GraduationCap className="mr-2 h-5 w-5" />
            Try 25 Free Questions
          </Button>
        </Link>
        <Link to="/signup" className="w-full sm:w-auto">
          <Button size="lg" variant="secondary" className="w-full sm:w-auto px-7">
            Create account
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <p className="mt-3 text-xs text-stone-500">
        No credit card. ~15 minutes for the trial.
      </p>
    </section>
  );
}

// ============================================================
//  TRUST STRIP — concrete numbers, tight row
// ============================================================

function TrustStrip() {
  // Live total question count from the public stats callable. While the
  // request is in flight or if it fails, we show "1,500+" as a graceful
  // floor — never the literal "—" that would erode trust on first paint.
  const stats = useQuery({
    queryKey: ['public-stats'],
    queryFn: () => callGetPublicStats({}),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const totalQuestions = stats.data?.totalQuestions;
  const totalDisplay = totalQuestions
    ? `${totalQuestions.toLocaleString()}+`
    : '1,500+';

  const items = [
    { v: '11k+', l: 'verified CNA facts' },
    { v: totalDisplay, l: 'practice questions' },
    { v: '70Q', l: 'Practice Exams' },
    { v: '24/7', l: 'practice access' },
  ];
  return (
    <section className="border-y border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
        {items.map((s) => (
          <div key={s.l}>
            <div className="text-2xl sm:text-3xl font-bold text-brand-700 dark:text-brand-400 tabular-nums">{s.v}</div>
            <div className="text-[11px] sm:text-xs uppercase tracking-wider text-stone-500 mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================
//  FEATURES — three short value props, equal weight
// ============================================================

function FeaturesSection() {
  return (
    <section className="px-4 sm:px-6 py-12 max-w-3xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Feature
          icon={<ShieldCheck className="h-5 w-5 text-brand-600" />}
          title="Real exam-style questions"
          body="The same clinical scenarios you'll see on the state CNA exam."
        />
        <Feature
          icon={<BarChart3 className="h-5 w-5 text-brand-600" />}
          title="Readiness score"
          body="A single number tells you how close you are to passing."
        />
        <Feature
          icon={<BookOpen className="h-5 w-5 text-brand-600" />}
          title="Teach-on-every-answer"
          body="Every question explains the right call — even when you got it."
        />
      </div>
    </section>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5">
      <div className="mb-2.5">{icon}</div>
      <h3 className="font-semibold mb-1 text-[15px]">{title}</h3>
      <p className="text-[13.5px] text-stone-600 dark:text-stone-400 leading-relaxed">{body}</p>
    </div>
  );
}

// ============================================================
//  HOW IT WORKS — 3 numbered steps as cards
// ============================================================

function HowItWorksSection() {
  const steps = [
    {
      n: 1,
      title: 'Start instantly',
      body: 'No email, no password. Open the trial and start answering.',
    },
    {
      n: 2,
      title: 'Answer 25 balanced questions',
      body: 'A mix of all 9 NNAAP categories, ramping from easy to harder.',
    },
    {
      n: 3,
      title: 'Get your readiness score',
      body: "We tell you exactly how ready you are — and where to focus next.",
    },
  ];
  return (
    <section id="how" className="border-y border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 scroll-mt-20">
      <div className="px-4 sm:px-6 py-14 max-w-3xl mx-auto">
        <SectionEyebrow>How the trial works</SectionEyebrow>
        <h2 className="text-2xl sm:text-3xl font-semibold mb-8 text-center tracking-tight">
          Three steps, about fifteen minutes
        </h2>
        <ol className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {steps.map((s) => (
            <li key={s.n} className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-950/40 p-5 relative">
              <span className="absolute -top-3 left-5 inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-600 text-white text-xs font-bold tabular-nums shadow-sm">
                {s.n}
              </span>
              <h3 className="mt-2 font-semibold text-[15px] mb-1">{s.title}</h3>
              <p className="text-[13.5px] text-stone-600 dark:text-stone-400 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex justify-center">
          <Link to="/trial/new">
            <Button size="lg" className="px-7">
              Start the free trial
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================================
//  PRICING — two clean cards, Pro highlighted
// ============================================================

function PricingSection() {
  return (
    <section id="pricing" className="px-4 sm:px-6 py-14 max-w-3xl mx-auto scroll-mt-20">
      <SectionEyebrow>Pricing</SectionEyebrow>
      <h2 className="text-2xl sm:text-3xl font-semibold mb-2 text-center tracking-tight">
        Free to try. Honest when you upgrade.
      </h2>
      <p className="text-center text-stone-600 dark:text-stone-400 mb-8 max-w-prose mx-auto text-[14.5px]">
        Use the trial to see if it works for you. Subscribe only if it does.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <PriceCard
          tone="trial"
          title="Trial"
          price="Free"
          sub="25 questions · no signup · readiness score"
          bullets={[
            'All 9 NNAAP categories sampled',
            'Instant feedback on every answer',
            'Personalised readiness score at the end',
          ]}
          cta={{ to: '/trial/new', label: 'Try 25 Free Questions' }}
        />
        <PriceCard
          tone="pro"
          title="Full Access"
          price="$49"
          priceSuffix="/ month"
          sub="Unlimited everything · cancel anytime"
          bullets={[
            'Unlimited practice across all 9 categories',
            'Full Practice Exams',
            'Per-category drills + readiness analytics',
            'Cancel from your account in two clicks',
          ]}
          cta={{ to: '/signup', label: 'Create account & subscribe' }}
          highlighted
        />
      </div>
      <p className="mt-6 text-center text-xs text-stone-500">
        Already have an account?{' '}
        <Link to="/signin" className="underline">Sign in</Link>
      </p>
    </section>
  );
}

function PriceCard({
  title,
  price,
  priceSuffix,
  sub,
  bullets,
  cta,
  tone,
  highlighted,
}: {
  title: string;
  price: string;
  priceSuffix?: string;
  sub: string;
  bullets: string[];
  cta: { to: string; label: string };
  tone: 'trial' | 'pro';
  highlighted?: boolean;
}) {
  return (
    <div
      className={
        'rounded-2xl border p-5 flex flex-col bg-white dark:bg-stone-900 transition-shadow ' +
        (highlighted
          ? 'border-brand-300 dark:border-brand-700 shadow-md ring-1 ring-brand-200/60 dark:ring-brand-800/60'
          : 'border-stone-200 dark:border-stone-800')
      }
    >
      <div className="flex items-center gap-2 mb-1">
        {tone === 'pro' ? (
          <Crown className="h-5 w-5 text-emerald-600" />
        ) : (
          <GraduationCap className="h-5 w-5 text-brand-600" />
        )}
        <h3 className="text-lg font-semibold">{title}</h3>
        {highlighted && (
          <span className="ml-auto text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
            Most popular
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-3xl font-bold tabular-nums">{price}</span>
        {priceSuffix && <span className="text-sm text-stone-500">{priceSuffix}</span>}
      </div>
      <p className="text-[13px] text-stone-600 dark:text-stone-400 mb-3">{sub}</p>
      <ul className="text-[13.5px] text-stone-700 dark:text-stone-300 space-y-1.5 mb-4 flex-1">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-none mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Link to={cta.to}>
        <Button size="lg" variant={highlighted ? 'primary' : 'secondary'} className="w-full">
          {cta.label}
        </Button>
      </Link>
    </div>
  );
}

// ============================================================
//  FOOTER + reusable eyebrow
// ============================================================

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-center mb-2">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-700 dark:text-brand-400">
        {children}
      </span>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/40">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500">
        <span>© {new Date().getFullYear()} Huduma CNA Exam</span>
        <nav className="flex items-center gap-4">
          <Link to="/trial/new" className="hover:text-stone-700 dark:hover:text-stone-300">Try free</Link>
          <Link to="/signup" className="hover:text-stone-700 dark:hover:text-stone-300">Sign up</Link>
          <Link to="/signin" className="hover:text-stone-700 dark:hover:text-stone-300">Sign in</Link>
          <a href="#pricing" className="hover:text-stone-700 dark:hover:text-stone-300">Pricing</a>
          <Link to="/privacy" className="hover:text-stone-700 dark:hover:text-stone-300">Privacy</Link>
        </nav>
      </div>
    </footer>
  );
}
